import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  ChevronRight,
  Cloud,
  FolderGit2,
  Loader2,
  MessageSquare,
  Server,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { hubApi, MACHINE_KIND_LABEL, MACHINE_KIND_ORDER } from "@/lib/hubApi";
import {
  store,
  useStore,
  type RemoteMachineState,
  type SessionSource,
  type StoreState,
} from "@/lib/store";
import type { RunningTask, Session } from "@/lib/types";
import { cn } from "@/lib/utils";
import { EmptyState, ViewLoading, ViewSection } from "@/views/shared";

// Mirrors the wake lifecycle in SessionList's NewSessionDialog: transient
// per-machine state that useHubStream's machine.status event supersedes.
// Whether a machine can be woken at all is no longer discovered here — the
// payload carries `wakeable`, so it is a render-time gate rather than the
// classification of a failed request.
type WakeState = "waking" | "error";

// views/shared's relativeTime takes an ISO string; machine timestamps arrive as
// epoch ms, so format them straight from the number rather than round-tripping
// number -> Date -> ISO -> Date just to reuse the string signature.
function relativeTimeFromEpoch(ms: number): string {
  if (!Number.isFinite(ms)) return "";
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ms).toLocaleDateString();
}

function formatElapsed(startedAt: number): string {
  const secs = Math.max(0, (Date.now() - startedAt) / 1000);
  if (secs < 1) return "<1s";
  if (secs < 60) return `${secs.toFixed(0)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${s}s`;
}

// runningTasks carry session keys ("local:<id>" / "<machineId>:<id>"); split
// them back apart so tasks can deep-link into the owning session.
function splitSessionKey(key: string): { source: SessionSource; id: string } {
  const sep = key.indexOf(":");
  if (sep < 0) return { source: "local", id: key };
  return { source: key.slice(0, sep), id: key.slice(sep + 1) };
}

function findTaskSession(
  state: StoreState,
  taskSessionKey: string,
): { source: SessionSource; slug: string; session: Session } | null {
  const { source, id } = splitSessionKey(taskSessionKey);
  const byProject =
    source === "local"
      ? state.sessionsByProject
      : state.remote[source]?.sessionsByProject ?? {};
  for (const [slug, sessions] of Object.entries(byProject)) {
    const session = sessions.find((s) => s.id === id);
    if (session) return { source, slug, session };
  }
  return null;
}

function OnlineDot({ online }: { online: boolean }) {
  return (
    <span
      className={cn(
        "h-2 w-2 shrink-0 rounded-full",
        online
          ? "bg-primary shadow-[0_0_6px_hsl(var(--primary))]"
          : "bg-muted-foreground/40",
      )}
    />
  );
}

interface MachineRowProps {
  entry: RemoteMachineState;
  isLocal: boolean;
  wakeState: WakeState | undefined;
  onWake: (machineId: string) => void;
}

function MachineRow({ entry, isLocal, wakeState, onWake }: MachineRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { machine } = entry;
  const isCloud = machine.kind === "cloud";
  const KindIcon = isCloud ? Cloud : Server;

  return (
    <div className="glass-border rounded-xl border bg-card/60">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${machine.hostname} details`}
        className="flex cursor-pointer items-start gap-3 rounded-xl p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          // Ignore keys bubbling out of the nested wake button.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-secondary/40">
          <KindIcon
            className={cn(
              "h-4 w-4",
              machine.online ? "text-primary" : "text-muted-foreground",
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <OnlineDot online={machine.online} />
            <span className="truncate text-sm font-medium text-foreground">
              {machine.hostname}
            </span>
            {isLocal && (
              <Badge
                variant="outline"
                className="shrink-0 border-primary/30 px-1.5 py-0 text-[9px] uppercase tracking-wide text-primary"
              >
                This machine
              </Badge>
            )}
          </div>
          <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
            {isCloud && machine.provider ? `${machine.provider} · ` : ""}
            {machine.os}/{machine.arch} · {machine.cpus} cpu · {machine.memoryGB} GB
            {machine.codezVersion ? ` · v${machine.codezVersion}` : ""}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-2.5 w-2.5" />
              {machine.activeSessionCount} active
            </span>
            <span className="inline-flex items-center gap-1">
              <FolderGit2 className="h-2.5 w-2.5" />
              {machine.repos.length} {machine.repos.length === 1 ? "repo" : "repos"}
            </span>
            {!machine.online && (
              <span>
                last seen {relativeTimeFromEpoch(machine.lastSeenAt)}
              </span>
            )}
          </div>
          {!machine.online && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              {!machine.wakeable ? (
                <p className="text-xs text-muted-foreground">
                  This machine cannot be woken remotely.
                </p>
              ) : wakeState === "error" ? (
                <p className="text-xs text-destructive">
                  Wake request failed. Try again later.
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={wakeState === "waking"}
                    onClick={() => onWake(machine.machineId)}
                    className="h-7 gap-1.5 border border-primary/30 text-xs text-primary hover:bg-primary/10"
                  >
                    {wakeState === "waking" ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Zap className="h-3 w-3" />
                    )}
                    {wakeState === "waking" ? "Waking…" : "Wake"}
                  </Button>
                  {wakeState === "waking" && (
                    <span className="text-xs text-muted-foreground">
                      Machine is starting up…
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <ChevronRight
          className={cn(
            "mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            expanded && "rotate-90",
          )}
        />
      </div>
      {expanded && (
        <div className="border-t border-border/40 px-3 py-2">
          {machine.repos.length === 0 ? (
            <p className="py-1 text-[11px] text-muted-foreground">No repos reported.</p>
          ) : (
            <ul className="space-y-1">
              {machine.repos.map((repo) => (
                <li key={repo.path} className="min-w-0">
                  <div className="truncate font-mono text-[11px] text-foreground">
                    {repo.path}
                  </div>
                  {repo.remote && (
                    <div className="truncate font-mono text-[10px] text-muted-foreground">
                      {repo.remote}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Compact reimplementation of TeamDashboard's task cards (it exports nothing
// reusable); the drawer stays untouched while this view absorbs its content.
function TaskCard({ task, state }: { task: RunningTask; state: StoreState }) {
  const target = findTaskSession(state, task.sessionId);
  const title =
    target?.session.title ||
    target?.session.id.slice(0, 12) ||
    splitSessionKey(task.sessionId).id.slice(0, 12);
  const running = task.state === "running";

  return (
    <button
      type="button"
      onClick={() => {
        if (target) void store.openSession(target.slug, target.session.id, target.source);
      }}
      className={cn(
        "w-full rounded-xl border border-l-4 p-3 text-left transition-colors",
        running
          ? "glass-border border-l-primary bg-card/80 hover:bg-secondary/30"
          : "border-border/40 border-l-border/40 bg-card/40 opacity-60 hover:bg-secondary/20",
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded border",
            running ? "border-primary/40 bg-primary/10" : "border-border/40 bg-muted/20",
          )}
        >
          <Bot className={cn("h-3 w-3", running ? "text-primary" : "text-muted-foreground")} />
        </div>
        <span
          className={cn(
            "truncate rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide",
            running ? "bg-primary/15 text-primary" : "text-muted-foreground",
          )}
        >
          {task.subagentType}
        </span>
        <span
          className={cn(
            "ml-auto flex shrink-0 items-center gap-1 font-mono text-[10px]",
            running
              ? "text-primary"
              : task.state === "error"
                ? "text-destructive"
                : "text-primary/60",
          )}
        >
          {running && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
          {running ? formatElapsed(task.startedAt) : task.state}
        </span>
      </div>
      {task.description && (
        <div className="mt-1 line-clamp-2 pl-7 text-xs text-foreground">
          {task.description}
        </div>
      )}
      <div className="mt-1 flex items-center gap-1 pl-7 text-[10px] text-muted-foreground">
        <ChevronRight className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">{title}</span>
      </div>
    </button>
  );
}

export function MachinesView() {
  const state = useStore();
  const [waking, setWaking] = useState<Record<string, WakeState>>({});
  const [, setTick] = useState(0);

  const hubActive = state.hubEnabled || state.hostedMode;
  const runningCount = state.runningTasks.filter((t) => t.state === "running").length;

  // Elapsed timers on running task cards need a heartbeat re-render.
  useEffect(() => {
    if (runningCount === 0) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [runningCount]);

  useEffect(() => {
    if (hubActive && !state.remoteMachinesLoaded) {
      void store.loadRemoteMachines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubActive]);

  // Grouped by stored kind, so the category axis is the payload's rather than
  // one improvised here. Empty categories are dropped, and a single remaining
  // category renders without a heading — a user with one laptop should not be
  // shown a taxonomy.
  const groups = useMemo(() => {
    const entries = Object.values(state.remote).sort((a, b) => {
      if (a.machine.online !== b.machine.online) return a.machine.online ? -1 : 1;
      return a.machine.hostname.localeCompare(b.machine.hostname);
    });
    const local = entries.filter((e) => e.machine.machineId === state.localMachineId);
    const remote = entries.filter((e) => e.machine.machineId !== state.localMachineId);
    const ordered = [...local, ...remote];

    return MACHINE_KIND_ORDER.map((kind) => ({
      kind,
      label: MACHINE_KIND_LABEL[kind],
      entries: ordered.filter((e) => e.machine.kind === kind),
    })).filter((group) => group.entries.length > 0);
  }, [state.remote, state.localMachineId]);

  const machineCount = groups.reduce((total, group) => total + group.entries.length, 0);

  async function triggerWake(machineId: string) {
    if (!state.hubToken) return;
    setWaking((prev) => ({ ...prev, [machineId]: "waking" }));
    try {
      await hubApi.wakeMachine(state.hubToken, machineId);
    } catch {
      setWaking((prev) => ({ ...prev, [machineId]: "error" }));
      return;
    }
    // The machine flips online via useHubStream's machine.status event; the
    // transient wake state just needs to expire.
    setTimeout(() => {
      setWaking((prev) => {
        const next = { ...prev };
        delete next[machineId];
        return next;
      });
    }, 8000);
  }

  if (hubActive && !state.remoteMachinesLoaded) {
    return <ViewLoading />;
  }

  const running = state.runningTasks.filter((t) => t.state === "running");
  const done = state.runningTasks.filter((t) => t.state !== "running");

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <ViewSection title="Machines">
        {!hubActive ? (
          <EmptyState message="Machines appear here when this CodeZero server is connected to CodeZ Hub. Run codez setup to link this machine and federate your fleet." />
        ) : machineCount === 0 ? (
          <EmptyState message="No machines registered yet. Machines show up once a CodeZero agent connects to the hub." />
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.kind} className="space-y-2">
                {groups.length > 1 && (
                  <h3 className="px-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </h3>
                )}
                {group.entries.map((entry) => (
                  <MachineRow
                    key={entry.machine.machineId}
                    entry={entry}
                    isLocal={
                      !state.hostedMode && entry.machine.machineId === state.localMachineId
                    }
                    wakeState={waking[entry.machine.machineId]}
                    onWake={(id) => void triggerWake(id)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </ViewSection>

      <ViewSection
        title="Active work"
        action={
          running.length > 0 ? (
            <span className="flex items-center gap-1 font-mono text-[10px] text-primary">
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
              {running.length} running
            </span>
          ) : undefined
        }
      >
        {state.runningTasks.length === 0 ? (
          <EmptyState message="No subagent tasks are running across your sessions." />
        ) : (
          <div className="space-y-2">
            {running.map((task) => (
              <TaskCard key={task.partId} task={task} state={state} />
            ))}
            {done.map((task) => (
              <TaskCard key={task.partId} task={task} state={state} />
            ))}
          </div>
        )}
      </ViewSection>
    </div>
  );
}
